import { motion } from "framer-motion";

const NODES = [
  { key: "cloud", label: "Cloud Storage", tag: "Public read access", icon: "cloud", x: 20, y: 30 },
  { key: "employee", label: "Employee Endpoint", tag: "Credentials phished", icon: "person", x: 80, y: 30 },
  { key: "server", label: "Legacy Server", tag: "CVE unpatched · 214d", icon: "dns", x: 20, y: 72 },
  { key: "darkweb", label: "Dark Web", tag: "3 mentions found", icon: "visibility_off", x: 80, y: 72 },
];

function AttackGraph() {
  return (
    <div className="absolute inset-0">
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
        {NODES.map((node) => (
          <line
            key={node.key}
            x1={50}
            y1={51}
            x2={node.x}
            y2={node.y}
            strokeWidth={0.5}
            stroke="rgba(239,68,68,0.55)"
            strokeDasharray="3 3"
            className="line-flow"
          />
        ))}
      </svg>

      <div className="absolute left-1/2 top-[51%] -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-2">
        <div className="flex items-center justify-center w-16 h-16 md:w-20 md:h-20 rounded-full border-2 border-severity-critical bg-severity-critical/10 text-severity-critical shadow-[0_0_28px_rgba(239,68,68,0.35)]">
          <span className="material-symbols-outlined text-3xl md:text-4xl">domain</span>
        </div>
        <span className="font-data-mono text-[10px] md:text-data-mono uppercase tracking-wider text-on-surface dark:text-surface-bright">
          Your Organization
        </span>
      </div>

      {NODES.map((node) => (
        <div
          key={node.key}
          className="absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-1.5"
          style={{ left: `${node.x}%`, top: `${node.y}%` }}
        >
          <div className="flex items-center justify-center w-11 h-11 md:w-14 md:h-14 rounded-full border-2 border-severity-critical bg-surface-container-lowest dark:bg-surface-dark text-severity-critical shadow-sm">
            <span className="material-symbols-outlined text-lg md:text-2xl">{node.icon}</span>
          </div>
          <span className="hidden sm:block font-data-mono text-[9px] md:text-[10px] uppercase tracking-wider text-on-surface-variant dark:text-surface-variant whitespace-nowrap">
            {node.label}
          </span>
          <span className="hidden md:block font-data-mono text-[9px] px-1.5 py-0.5 rounded-full border border-severity-critical/30 bg-severity-critical/10 text-severity-critical whitespace-nowrap">
            {node.tag}
          </span>
        </div>
      ))}
    </div>
  );
}

export default function AttackNarrative() {
  return (
    <section className="w-full px-gutter max-w-container-max mx-auto mb-section-gap">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.4 }}
        transition={{ duration: 0.5 }}
        className="text-center mb-stack-lg"
      >
        <span className="font-eyebrow text-eyebrow text-secondary uppercase tracking-widest block mb-2">
          The Narrative
        </span>
        <h2 className="font-headline-section text-headline-section text-on-surface dark:text-surface-bright">
          The Anatomy of an Attack
        </h2>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.55 }}
        className="relative w-full h-[520px] md:h-[600px] rounded-xl overflow-hidden border border-glass bg-surface-card-light dark:bg-surface-card-dark backdrop-blur-lg shadow-sm bg-grid-pattern"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-secondary/10 pointer-events-none" />

        <AttackGraph />

        <div className="absolute inset-0 p-6 md:p-8 flex flex-col justify-between pointer-events-none">
          <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
            <div className="bg-surface-container-lowest/80 dark:bg-black/50 backdrop-blur-md border border-glass rounded-lg p-4 shadow-sm">
              <span className="font-label-sm text-label-sm text-on-surface-variant dark:text-surface-variant block mb-1">
                LIVE RISK SCORE
              </span>
              <div className="flex items-baseline gap-2">
                <span className="font-data-mono text-[48px] font-bold text-severity-critical leading-none">92</span>
                <span className="font-data-mono text-data-mono text-severity-critical flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-severity-critical pulse-dot" />
                  CRITICAL
                </span>
              </div>
            </div>
            <div className="flex flex-col gap-2 w-full sm:w-auto">
              <div className="bg-error-container border border-severity-critical/20 rounded px-3 py-1 flex items-center gap-2 w-fit">
                <span className="material-symbols-outlined text-[16px] text-severity-critical">warning</span>
                <span className="font-data-mono text-data-mono text-on-error-container uppercase">
                  Unpatched CVE-2026
                </span>
              </div>
              <div className="bg-surface-container dark:bg-white/10 border border-severity-warning/20 rounded px-3 py-1 flex items-center gap-2 w-fit">
                <span className="material-symbols-outlined text-[16px] text-severity-warning">key</span>
                <span className="font-data-mono text-data-mono text-severity-warning uppercase">
                  Leaked Credentials
                </span>
              </div>
            </div>
          </div>

          <div className="self-center bg-surface-container-lowest/90 dark:bg-black/60 backdrop-blur-lg border border-glass rounded-full px-6 py-3 shadow-sm flex items-center gap-4">
            <span className="font-data-mono text-data-mono text-on-surface-variant dark:text-surface-variant flex items-center gap-2">
              <span className="material-symbols-outlined text-primary text-[18px]">radar</span>
              SCANNING THREAT VECTOR...
            </span>
          </div>
        </div>
      </motion.div>
    </section>
  );
}
